/**
 * AI Field Detection Service
 *
 * Service for calling backend AI API to analyze PDF text and suggest form fields
 *
 * Linear Issue: AIF-5
 */

import axios from 'axios';
import constants from '../components/constants.json';

const { inputFieldOptions } = constants;

/**
 * Get supported field types from constants.json
 * Maps field values to their Hebrew labels
 *
 * @returns {Array} Array of field type definitions with value and label
 */
export function getSupportedFieldTypes() {
  return inputFieldOptions.map(option => ({
    value: option.value,
    label: option.label
  }));
}

/**
 * Call backend AI API to detect form fields in extracted PDF text
 *
 * @param {Object} extractionData - Data from PDF text extraction
 * @param {Array} extractionData.textItems - Array of text items with coordinates
 * @param {Array} extractionData.lines - Grouped text lines
 * @param {Array} extractionData.labels - Detected field labels
 * @returns {Promise<Array>} Array of field suggestions from AI
 *
 * Each suggestion contains:
 * - fieldType: string - Field type in document language (e.g., 'שם הילד' for Hebrew, 'childName' for English)
 * - fieldTypeLabel: string - Hebrew label (e.g., 'שם הילד')
 * - fieldTypeValue: string - English value (e.g., 'childName')
 * - label: string - The detected label text
 * - x: number - X coordinate
 * - y: number - Y coordinate
 * - width: number - Suggested field width
 * - height: number - Suggested field height
 * - page: number - Page number
 * - confidence: number - AI confidence score (0-1)
 * - reasoning: string - AI's explanation for the suggestion
 * - dir: string - Text direction ('ltr' or 'rtl')
 *
 * Backend detects document language automatically and returns field types in that language.
 */
export async function detectFieldsWithAI(extractionData) {
  try {
    const { textItems, lines, labels } = extractionData;

    // Validate input
    if (!textItems || textItems.length === 0) {
      throw new Error('No text items provided for AI analysis');
    }

    console.log('[AI Detection] Sending request to backend...', {
      textItems: textItems.length,
      lines: lines?.length || 0,
      labels: labels?.length || 0
    });

    // Get AI backend URL from config (use AI-specific backend)
    const backendURL = window.AppConfig.aiBackendURL || window.AppConfig.serverDomain;
    const endpoint = `${backendURL}/api/ai/detect-fields`;

    // Get supported field types to send to backend
    const supportedFieldTypes = getSupportedFieldTypes();

    // Send to backend AI API with field types for language-aware detection
    const response = await axios.post(
      endpoint,
      {
        textItems,
        lines,
        detectedLabels: labels,
        supportedFieldTypes // NEW: Enable multilingual field type responses
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 30000, // 30 second timeout
      }
    );

    console.log('[AI Detection] Received response:', response.data);

    // Log detected language
    const detectedLanguage = response.data.metadata?.detectedLanguage;
    if (detectedLanguage) {
      console.log(`[AI Detection] Detected language: ${detectedLanguage}`);
    }

    // Extract suggestions from response
    const suggestions = response.data.suggestions || [];

    // Map suggestions to include full field type info
    // Backend now returns language-aware field types:
    // - Hebrew PDFs: fieldType = "שם הילד" (Hebrew label)
    // - English PDFs: fieldType = "childName" (English value)
    const mappedSuggestions = suggestions.map(suggestion => {
      // Try to find match by value (English) OR label (Hebrew)
      const fieldType = inputFieldOptions.find(
        option => option.value === suggestion.fieldType || option.label === suggestion.fieldType
      );

      return {
        ...suggestion,
        fieldTypeLabel: fieldType?.label || suggestion.fieldType,
        fieldTypeValue: fieldType?.value || suggestion.fieldType,
      };
    });

    console.log('[AI Detection] Mapped suggestions:', mappedSuggestions.length);

    return mappedSuggestions;

  } catch (error) {
    console.error('[AI Detection] Error:', error);

    // Provide helpful error messages
    if (error.code === 'ECONNABORTED') {
      throw new Error('AI analysis timed out. Please try again with a smaller PDF or check your connection.');
    }

    if (error.response?.status === 400) {
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Invalid request';
      throw new Error(`Backend validation error: ${errorMsg}`);
    }

    if (error.response?.status === 404) {
      throw new Error('AI detection endpoint not found. Please ensure backend is deployed correctly.');
    }

    if (error.response?.status === 500) {
      const errorMsg = error.response?.data?.error || 'Internal server error';
      throw new Error(`AI analysis failed: ${errorMsg}`);
    }

    if (error.response?.status === 503) {
      throw new Error('AI service is temporarily unavailable. Please try again in a moment.');
    }

    // Generic error
    throw new Error(`AI field detection failed: ${error.message}`);
  }
}

/**
 * Analyze PDF and get AI field suggestions
 * Combines text extraction and AI detection in one call
 *
 * @param {Object} extractionData - Data from PDF text extraction service
 * @returns {Promise<Object>} Analysis results with suggestions and metadata
 */
export async function analyzePDFWithAI(extractionData) {
  try {
    const startTime = Date.now();

    // Call AI detection
    const suggestions = await detectFieldsWithAI(extractionData);

    const processingTime = Date.now() - startTime;

    console.log('[AI Analysis] Complete:', {
      suggestions: suggestions.length,
      processingTime: `${processingTime}ms`
    });

    return {
      suggestions,
      metadata: {
        processingTime,
        textItems: extractionData.textItems?.length || 0,
        detectedLabels: extractionData.labels?.length || 0,
        timestamp: new Date().toISOString()
      }
    };

  } catch (error) {
    console.error('[AI Analysis] Error:', error);
    throw error;
  }
}

/**
 * Get health status of AI detection service
 *
 * @returns {Promise<Object>} Health check response
 */
export async function checkAIServiceHealth() {
  try {
    const backendURL = window.AppConfig.aiBackendURL || window.AppConfig.serverDomain;
    const endpoint = `${backendURL}/api/ai/detect-fields/health`;

    const response = await axios.get(endpoint, { timeout: 5000 });

    return response.data;

  } catch (error) {
    console.error('[AI Health Check] Error:', error);
    throw new Error(`AI service health check failed: ${error.message}`);
  }
}

export default {
  getSupportedFieldTypes,
  detectFieldsWithAI,
  analyzePDFWithAI,
  checkAIServiceHealth
};
